import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  schema?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  schema,
}: SEOHeadProps) {
  const fullTitle = title.includes('韩漫星球') ? title : `${title} - 韩漫星球`;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('og:title', ogTitle || fullTitle, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:type', ogType, true);
    if (canonicalUrl) {
      setMeta('og:url', canonicalUrl, true);
      let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute('href', canonicalUrl);
    }
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle || fullTitle);
    setMeta('twitter:description', ogDescription || description);

    let schemaEl: HTMLScriptElement | null = null;
    if (schema) {
      const existing = document.querySelector('script[data-dynamic-schema]');
      if (existing) existing.remove();
      schemaEl = document.createElement('script');
      schemaEl.type = 'application/ld+json';
      schemaEl.setAttribute('data-dynamic-schema', 'true');
      schemaEl.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaEl);
    }

    return () => {
      if (schemaEl) schemaEl.remove();
    };
  }, [fullTitle, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, schema]);

  return null;
}